import { create } from "zustand";
import { persist } from "zustand/middleware";
import { resolvePaymentRegion } from "@/utils/paymentRegion";

export type ServiceType = "Delivery" | "Collection";

export interface StoreDetails {
  id: number;
  externalStoreId: string;
  name: string;
  address: string;
  city: string;
  country: string;
  region: string;
  postcode: string;
  phoneNumber: string;
  email: string;
  latitude: number;
  longitude: number;
  openingHour: string;
  closingHour: string;
  operatingHours: Record<string, unknown> | null;
  isActive: boolean;
  orderingStatus: string;
  franchiseId: number;
  franchiseOwnerId: number;
  storeManagerName: string | null;
  storeManagerPhone: string | null;
  redirectToStoreId: string | null;
  redirectMessage: string | null;
  redirectExpiry: string | null;
  createdByUserId: string | null;
  lastStatusUpdatedByRole: string | null;
  lastStatusUpdatedAt: string | null;
  lastStatusUpdatedByUserId: string | null;
  collectionTimeMode: string;
  deliveryTimeMode: string;
  welcomeMessage: Record<string, unknown> | null;
  fsaRating: number | null;
  isDeleted: boolean;
  deletedAt: string | null;
  emergencyModeEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  hygieneRating?: number | string | null;
  openingTimes?: Record<string, unknown>;
  voucher_management_flagged?: boolean;
  deliveryCharge?: string | number | null;
  deliveryEligibilityMinSpend?: string | number | null;
  freeDeliveryMinSpend?: string | number | null;
  standardDeliveryCharge?: string | number | null;
}

export interface StoreSelectionState {
  postcode: string;
  storeName: string;
  storeId?: string;
  service: ServiceType;
  hasConfirmedPostcode: boolean;
  region?: string;
  currencySymbol?: string;
  storeDetails?: StoreDetails | null;
  /** Whether the searched postcode falls within the store's delivery catchment area */
  deliveryAvailable?: boolean;
}

interface StoreSelectionActions {
  setPostcode: (postcode: string) => void;
  setStore: (storeName: string, storeId?: string, region?: string) => void;
  setService: (service: ServiceType) => void;
  setAll: (data: Partial<StoreSelectionState>) => void;
  setStoreDetails: (details: StoreDetails | null) => void;
  clearStore: () => void;
}

type StoreSelectionStore = StoreSelectionState & StoreSelectionActions;

const DEFAULT_STATE: StoreSelectionState = {
  postcode: "",
  storeName: "Select a store",
  storeId: undefined,
  service: "Delivery",
  hasConfirmedPostcode: false,
};

const LEGACY_KEYS = [
  "selectedStoreId",
  "orderType",
  "postcode",
  "storeName",
  "postcode_saved",
] as const;

function migrateLegacyLocalStorage(): Partial<StoreSelectionState> | null {
  try {
    const legacyStoreId = localStorage.getItem("selectedStoreId");
    const legacyOrderType = localStorage.getItem("orderType");
    const legacyPostcode = localStorage.getItem("postcode");
    const legacyStoreName = localStorage.getItem("storeName");
    const legacyPostcodeSaved = localStorage.getItem("postcode_saved");

    // Only migrate if at least one legacy key exists
    if (
      !legacyStoreId &&
      !legacyOrderType &&
      !legacyPostcode &&
      !legacyStoreName &&
      !legacyPostcodeSaved
    ) {
      return null;
    }

    const migrated: Partial<StoreSelectionState> = {};

    if (legacyStoreId) migrated.storeId = legacyStoreId;
    if (legacyStoreName) migrated.storeName = legacyStoreName;
    if (legacyPostcode) migrated.postcode = legacyPostcode;
    if (legacyOrderType) {
      migrated.service =
        legacyOrderType.toLowerCase() === "delivery"
          ? "Delivery"
          : "Collection";
    }
    if (legacyPostcodeSaved === "1") {
      migrated.hasConfirmedPostcode = true;
    }

    // Clear legacy keys after migration
    LEGACY_KEYS.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch {
        /* ignore */
      }
    });

    return migrated;
  } catch {
    // localStorage unavailable
    return null;
  }
}

export const useStoreSelectionStore = create<StoreSelectionStore>()(
  persist(
    (set) => ({
      ...DEFAULT_STATE,

      setPostcode: (postcode) => {
        set({ postcode });
      },

      setStore: (storeName: string, storeId?: string, region?: string) => {
        const currencySymbol = region === "ROI" ? "€" : "£";
        set({ storeName, storeId, region, currencySymbol });
      },

      setService: (service) => {
        set({ service });
      },

      setAll: (data) => {
        set((state) => {
          const updatedState = { ...state, ...data };
          // Ensure currencySymbol is always set based on region
          if (updatedState.region && !updatedState.currencySymbol) {
            updatedState.currencySymbol =
              updatedState.region === "ROI" ? "€" : "£";
          }
          return updatedState;
        });
      },

      setStoreDetails: (details) => {
        const resolvedRegion = details
          ? resolvePaymentRegion(details.region, details.country)
          : undefined;
        set({
          storeDetails: details,
        });
        // Update region and currency when store details are set
        if (details) {
          const currencySymbol = resolvedRegion === "ROI" ? "€" : "£";
          set({ region: resolvedRegion, currencySymbol });
        }
      },

      clearStore: () => {
        set(DEFAULT_STATE);
      },
    }),
    {
      name: "store_ctx",
      storage: {
        getItem: (name) => {
          // Try sessionStorage first, then migrate from old localStorage
          let str = sessionStorage.getItem(name);
          if (!str) {
            try {
              const oldData = localStorage.getItem(name);
              if (oldData) {
                sessionStorage.setItem(name, oldData);
                localStorage.removeItem(name);
                str = oldData;
              }
            } catch {
              /* ignore */
            }
          }
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
      onRehydrateStorage: () => (state) => {
        if (!state) return;

        // Ensure currencySymbol is always set based on region
        if (state.region && !state.currencySymbol) {
          state.currencySymbol = state.region === "ROI" ? "€" : "£";
        }

        const isDefaultState =
          !state.storeId &&
          state.storeName === "Select a store" &&
          !state.postcode &&
          !state.hasConfirmedPostcode;

        if (isDefaultState) {
          // Try legacy individual localStorage keys (selectedStoreId, orderType, etc.)
          const migrated = migrateLegacyLocalStorage();
          if (migrated) {
            useStoreSelectionStore.setState((s) => ({ ...s, ...migrated }));
          }
        }
      },
    },
  ),
);

// Selector hooks for optimized re-renders
export const selectPostcode = (state: StoreSelectionStore) => state.postcode;
export const selectStoreName = (state: StoreSelectionStore) => state.storeName;
export const selectStoreId = (state: StoreSelectionStore) => state.storeId;
export const selectService = (state: StoreSelectionStore) => state.service;
export const selectHasConfirmedPostcode = (state: StoreSelectionStore) =>
  state.hasConfirmedPostcode;
export const selectRegion = (state: StoreSelectionStore) => state.region;
export const selectCurrencySymbol = (state: StoreSelectionStore) =>
  state.currencySymbol;
export const selectStoreDetails = (state: StoreSelectionStore) =>
  state.storeDetails;
export const selectVoucherManagementFlagged = (state: StoreSelectionStore) =>
  state.storeDetails?.voucher_management_flagged === true;
export const selectDeliveryAvailable = (state: StoreSelectionStore) =>
  state.deliveryAvailable;
